import React, { useEffect, useRef, useState } from "react";
import { MakeMappingViewModel, makeMappingAPI } from "../api/makeMappingAPI";
import { MakeModel, makeAPI } from "../api/makeAPI";
import { Form, InputGroup, Table } from "react-bootstrap";
import MakeMappingsModal from "../components/modals/MakeMappingsModal";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Problem } from "../api/backendApi";
import { TrashFill, PlusCircleFill, Search } from "react-bootstrap-icons";
import { dataProviderApi, DataProviderModel } from "../api/dataProviderApi";

function MakeMappingsPage() {
  const [mappings, setMappings] = useState<MakeMappingViewModel[]>([]);
  const [crsMakes, setCrsMakes] = useState<MakeModel[]>([]);
  const [filteredMappings, setFilteredMappings] = useState<
    MakeMappingViewModel[]
  >([]);
  const [makeNameFilters, setMakeNameFilters] = useState<string[]>([""]);
  const [providers, setProviders] = useState<DataProviderModel[]>([]);
  const [providerFilter, setProviderFilter] = useState<string>("");
  const [unmappedFilter, setUnmappedFilter] = useState<boolean>(false);
  const [isCreate, setIsCreate] = useState(false);
  const [modalText, setModalText] = useState("");

  const [newMapping, setNewMapping] = useState<
    MakeMappingViewModel | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [mappingBeingEdited, setMappingBeingEdited] = useState<
    MakeMappingViewModel | undefined
  >(undefined);
  const [scrollToEnd, setScrollToEnd] = useState<boolean>(false);
  const [show, setShow] = useState(false);

  const firstUpdate = useRef(true);

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    mapping?: MakeMappingViewModel,
  ) => {
    setIsCreate(isCreate);
    setModalText(modalText);
    setNewMapping(mapping);
    setShow(true);
  };

  const filterMappings = (mappingsToUse?: MakeMappingViewModel[]) => {
    var filteredMappings = [...(mappingsToUse ?? mappings)];
    makeNameFilters.forEach((makeNameFilter) => {
      filteredMappings = filteredMappings.filter((mapping) =>
        unmappedFilter
          ? mapping.dataProviderMakeDescription
              ?.normalize("NFD")
              .replace(/\p{Diacritic}/gu, "")
              .toUpperCase()
              .startsWith(makeNameFilter.toUpperCase()) &&
            mapping.dataProviderName
              .toUpperCase()
              .startsWith(providerFilter.toUpperCase()) &&
            mapping.crsMakes.length === 0 &&
            mapping.isSupported
          : mapping.dataProviderMakeDescription
              ?.normalize("NFD")
              .replace(/\p{Diacritic}/gu, "")
              .toUpperCase()
              .startsWith(makeNameFilter.toUpperCase()) &&
            mapping.dataProviderName
              .toUpperCase()
              .startsWith(providerFilter.toUpperCase()),
      );
    });
    setFilteredMappings(filteredMappings);
  };

  useEffect(() => {
    filterMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeNameFilters]);

  useEffect(() => {
    filterMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  useEffect(() => {
    filterMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unmappedFilter]);

  useEffect(() => {
    async function loadMakeMappings() {
      setLoading(true);
      const data = await makeMappingAPI.getMakeMappings();

      if (data.ok) {
        setError(undefined);
        setMappings(data.value);
        setFilteredMappings(data.value);
        loadDataProviders();
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadMakeMappings();
  }, []);

  async function loadDataProviders() {
    const data = await dataProviderApi.getDataProviders();

    if (data.ok) {
      setError(undefined);
      setProviders(data.value);
    } else {
      setError(data.problem);
    }
  }

  useEffect(() => {
    async function loadCrsMakes() {
      const data = await makeAPI.getMakes();

      if (data.ok) {
        setError(undefined);
        setCrsMakes(data.value);
      } else {
        setError(data.problem);
      }
    }
    loadCrsMakes();
  }, []);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else if (scrollToEnd) {
      var table = document.querySelector("table");
      table?.scrollTo(0, table?.scrollHeight * 2);
      setScrollToEnd(false);
    }
  }, [scrollToEnd]);

  const deleteMapping = (model: MakeMappingViewModel) => {
    makeMappingAPI
      .deleteMakeMapping(
        model.dataProviderId ?? 0,
        model.dataProviderMakeId,
        model.dataProviderMakeDescription,
      )
      .then((response) => {
        if (response.ok) {
          setMappings(mappings.filter((m) => m !== model));
          setFilteredMappings(filteredMappings.filter((m) => m !== model));
        } else {
          setError(response.problem);
        }
      });
  };

  const onMappingChanged = (mapping: MakeMappingViewModel) => {
    setError(undefined);
    let updatedMappings = mappings.map((m: MakeMappingViewModel) => {
      return m === mappingBeingEdited ? mapping : m;
    });

    setMappings(updatedMappings);
    setMappingBeingEdited(undefined);
    filterMappings(updatedMappings);
  };

  const onNewMapping = (mapping: MakeMappingViewModel) => {
    const updatedMappings = [...mappings, mapping];
    setMappings(updatedMappings);
    setScrollToEnd(true);
    setNewMapping(undefined);
    filterMappings(updatedMappings);
  };

  return (
    <>
      <div className="main-container-title">
        <h1>
          Make Mappings
          <PlusCircleFill
            className="text-primary"
            onClick={() => handleShow(true, "Add Make", undefined)}
            width={32}
            height={32}
            style={{ marginLeft: "0.5rem" }}
          />
        </h1>

        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "100%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover>
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>
                      <InputGroup>
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                          }}
                        >
                          Provider
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          onChange={(e) => {
                            var filter = e.target.value;
                            if (filter === "All") {
                              filter = "";
                            }
                            setProviderFilter(filter);
                          }}
                        >
                          <option>All</option>
                          {providers.map((provider) => (
                            <option key={provider.id}>{provider.name}</option>
                          ))}
                        </Form.Select>
                      </InputGroup>
                    </th>
                    <th style={{ width: "55%" }}>
                      <InputGroup className="w-75">
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                          }}
                        >
                          Provider Make
                        </Form.Label>
                        <span className="input-group-text">
                          <Search width={12} height={12} />
                        </span>
                        <Form.Control
                          size="sm"
                          onChange={(e) => {
                            var filters = e.target.value.split(" ");
                            setMakeNameFilters(filters);
                          }}
                        ></Form.Control>
                      </InputGroup>
                    </th>
                    <th style={{ width: "20%" }}>
                      <div
                        className="table-header-red"
                        onClick={() => setUnmappedFilter(!unmappedFilter)}
                      >
                        <Form.Check>
                          <Form.Check.Input
                            checked={unmappedFilter}
                            onChange={() => setUnmappedFilter(!unmappedFilter)}
                            type="checkbox"
                            isInvalid
                          ></Form.Check.Input>
                          <Form.Check.Label style={{ color: "black" }}>
                            CRS Make Description
                          </Form.Check.Label>
                        </Form.Check>
                      </div>
                    </th>
                    <th style={{ width: "5%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMappings.map((mapping, index) => (
                    <tr
                      onClick={() => {
                        setMappingBeingEdited(mapping);
                        handleShow(false, "Edit Make Mapping", mapping);
                      }}
                      key={index}
                    >
                      <>
                        <td>{mapping.dataProviderName}</td>
                        <td>
                          {mapping.dataProviderMakeId
                            ? "ID: " + mapping.dataProviderMakeId
                            : "Name: " + mapping.dataProviderMakeDescription}
                        </td>
                        <td>
                          {mapping.crsMakes.length > 0 ? (
                            <ul>
                              {mapping.crsMakes?.map((crsMake, index) => (
                                <li key={index}>
                                  {crsMake.crsMakeDescription}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-danger">
                              {mapping.isSupported
                                ? "Unmapped"
                                : "Not available in CRS"}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <TrashFill
                            className="text-danger"
                            height={24}
                            width={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("Are you sure?")) {
                                deleteMapping(mapping);
                              }
                            }}
                          ></TrashFill>
                        </td>
                      </>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {show ? (
        <MakeMappingsModal
          mapping={
            newMapping ?? {
              dataProviderId: 0,
              dataProviderName: "",
              dataProviderMakeId: undefined,
              dataProviderMakeDescription: undefined,
              isSupported: undefined,
              crsMakes: [],
            }
          }
          dataProviders={providers}
          crsMakes={crsMakes}
          onChange={isCreate ? onNewMapping : onMappingChanged}
          onCancel={() => {
            setNewMapping(undefined);
            setShow(false);
          }}
          isCreate={isCreate}
          modalText={modalText}
        />
      ) : null}
    </>
  );
}

export default MakeMappingsPage;
